import {Store} from '@ngrx/store';

import {Dictionary} from '@ngrx/entity';
import {TableVirtualScrollDataSource} from 'ng-table-virtual-scroll';
import {BehaviorSubject, combineLatest, from, Observable, of, Subscription} from 'rxjs';
import {
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';
import {
    getCommentByEntityTypeEntityId,
    getCommentFetched,
} from 'src/app/dave-data-module/State/selectors/comment.selector';
import {CommissionEntity} from '../../../dave-data-module/entities/commission.entity';
import { EntityRoleSlugEnum, EntityTypesEnum } from '../../../dave-data-module/entities/entity-role.entity';
import {UserToCommissionEntity} from '../../../dave-data-module/entities/user-to-commission.entity';
import {UserEntity} from '../../../dave-data-module/entities/user.entity';
import {getFetched$} from '../../../dave-data-module/helper/helper';
import {State} from '../../../dave-data-module/State';
import {
    getCommissionsFetched,
    getCommissionSorted,
} from '../../../dave-data-module/State/selectors/commission.selector';
import {
    getCommissionTypeDictionary,
    getCommissionTypesFetched,
} from '../../../dave-data-module/State/selectors/commissionType.selectors';
import {getEntityRole, getEntityRoleFetched} from '../../../dave-data-module/State/selectors/entity-role.selector';
import {
    getStatusFromBackoffice,
    getStatusFromBackofficeFetched,
} from '../../../dave-data-module/State/selectors/statusFromBackoffice.selectors';
import {
    getUserToCommissionCommissionIdsMap,
    getUserToCommissionDictionary,
    getUserToCommissionFetched,
} from '../../../dave-data-module/State/selectors/user-to-commission.selector';
import { getUser, getUserDictionary, getUsersFetched } from '../../../dave-data-module/State/selectors/users.selectors';
import {DaveListTableData} from '../../../dave-list/components/dave-list/dave-list.component';
import {
    IFilterTypeSearchMultiSelectValue,
} from '../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import {
    getAddressString,
    getCommissionColorValue,
    isNotNullOrUndefined, SearchQueriesDebounceTime,
    stringSearch,
    uniqArray,
} from '../../../helper/helper';

import {
    LedgerImportDocumentTypes, LedgerImportEntity,
    LedgerImportStatusEnum,
} from 'src/app/dave-data-module/entities/ledger-import.entity';

import {formatCurrency, getCurrencySymbol} from '@angular/common';
import {isArray} from 'chart.js/helpers';
import {CommentEntityTypeEnum} from '../../../dave-data-module/entities/comment.entity';
import {CommentResolver} from '../../../dave-data-module/guards/comment.resolver';
import {CustomLabelService} from '../../../services/custom-label/custom-label.service';
import {FilterTypes} from '../../../services/default-filter.service';
import {
    getDepartmentDictionary,
    getDepartmentFetched,
} from '../../../dave-data-module/State/selectors/department.selector';
import {
    getPartnerOfficeDictionary,
    getPartnerOfficesFetched,
} from '../../../dave-data-module/State/selectors/partners.selectors';
import {DepartmentEntity} from '../../../dave-data-module/entities/department.entity';
import {CustomerDataService} from '../../../dave-data-module/services/customer-data.service';
import {MatSort} from "@angular/material/sort";
import {MatLegacyPaginator} from "@angular/material/legacy-paginator";
import {LedgerImportDataService} from "../../../dave-data-module/services/ledger-import-data.service";

function getUserNamesByRoleId(u2csByCommission: UserToCommissionEntity[], roleId: number, users: Dictionary<UserEntity>) {
    return uniqArray(u2csByCommission.filter((u2c) => u2c.RoleId === roleId).map((u2c) => u2c.UserId))
        .map((uId) => users[uId])
        .filter((user) => user && !user.Deleted)
        .map((user) => user.DisplayName)
        .join(' | ');
}

export interface CommissionListItem extends DaveListTableData {
    AuftragsNr: string;
    InterneNr: string;
    SchadenNr: string;
    VersicherungsNr: string;
    StatusBackoffice: string;
    Auftraggeber: string;
    LastDeadline: string;
    AbgabeterminAG: string;
    // Projektleiter: string;
    Auftrag: CommissionEntity;
    StartDate: string;
    EndDate: string;
    Address: string;
    // Vorarbeiter: string;
    // Sachbearbeiter: string;
    // routerLink: string | any[];
    Auftragstyp: string;
    cssStyle: string;
    cssClass: string;

    CommissionTypeId: number;
    CustomerId: number;
    AssignUserIds: number[];
    UserId: number;
    PartnerOffice: string;
    PartnerOfficeId: number;
    StatusBackofficeId: number;
    Description: string;
    Author: string;
    CreatedAt: string;
    roleCols: { [key: string]: string };
    lastComment$: Observable<string>;
    offerSum$: Observable<string>;
    id: number;
    isChildCommission: boolean;
    isParentCommission: boolean;
    isParentCommissionTooltip: string;
    freeTextField1: string;
    colorBarValue: string;
    OfferSubmissionDate: string;
    NegotiationAt: string;
    Department: string;
    DepartmentId: number;
    ChildCommissionDepartments: string;
    CostCenter: string;
    hasRelevantRoleOrIsAssigned: boolean;
    CompleteBusinessVolume: string;
    RelevantUserIds: number[];
}

/**
 * Data source for the CommissionList view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class CommissionListDataSource extends TableVirtualScrollDataSource<CommissionListItem> {
    public static readonly roleKeyPrefix = 'roleId_';

    public marSort: MatSort;
    public matPaginator: MatLegacyPaginator;

    public searchQuery$ = new BehaviorSubject<string>('');
    private commentsAfterFetched$ = getFetched$(this.store, getCommentFetched, getCommentFetched, this.commentResolver).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    commissionEntityRoles$ = this.store.select(getEntityRoleFetched).pipe(
        filter((v) => v),
        switchMap(() => this.store.select(getEntityRole)),
        map((roles) => roles.filter((r) => r.Entity === EntityTypesEnum.Commission)),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    commissionData$ = getFetched$(this.store, getCommissionsFetched, getCommissionSorted).pipe(
        map((cs) => cs?.filter((c) => !c.Deleted)),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    data$ = combineLatest([
        this.commissionData$,
        getFetched$(this.store, getCommissionTypesFetched, getCommissionTypeDictionary),
        getFetched$(this.store, getUsersFetched, getUserDictionary),
        // getFetched$(this.store, getCustomersFetched, getCustomerDictionary),
        this.commissionData$.pipe(
            map(commissions => commissions.map(c => c.CustomerId).filter(isNotNullOrUndefined)),
            switchMap((customerIds) => this.customerDataService.GetCustomersDict$(customerIds)),
        ),
        getFetched$(this.store, getDepartmentFetched, getDepartmentDictionary),
        combineLatest([
            getFetched$(this.store, getStatusFromBackofficeFetched, getStatusFromBackoffice),
            getFetched$(this.store, getUserToCommissionFetched, getUserToCommissionCommissionIdsMap).pipe(switchMap((u2cCIdsMap) => this.store.select(getUserToCommissionDictionary).pipe(map((u2cDict) => ({ u2cDict, u2cCIdsMap }))))),
            this.commissionEntityRoles$,
            this.cls.getSingle$('Commission'),
            getFetched$(this.store, getPartnerOfficesFetched, getPartnerOfficeDictionary),
        ]),
        this.store.select(getUser),
        this.store.select(getEntityRole),
    ]).pipe(
        map(([commissions, commissonTypes, users, customers, departments, [boStates, { u2cDict, u2cCIdsMap }, roles, singleCommissionLabel, partnerOffices], user, entityRoles]) => {
            const commissionsWithParents: CommissionEntity[] = commissions.filter((c) => !!c.ParentId);

            const unwantedRoleNames = [EntityRoleSlugEnum.Admin, EntityRoleSlugEnum.Nutzer, EntityRoleSlugEnum.Leser];
            const ignoredRoles = entityRoles.filter(r => unwantedRoleNames.some(s => s === r.Slug)).map(r => r.Id);

            return commissions.map<CommissionListItem>((commission) => {
                const routerLink = [commission.Id];
                const now = new Date();
                now.setDate(now.getDate() + 2);
                const StatusBackoffice = boStates.find((s) => s.Id === commission.StatusBackofficeId);
                const u2csByCommission = (u2cCIdsMap.get(commission.Id) || []).filter(isNotNullOrUndefined).map((id) => u2cDict[id]);
                const customer = commission.CustomerId && customers[commission.CustomerId];

                const roleCols: { [key: string]: string } = {};
                roles.forEach((r) => {
                    roleCols[CommissionListDataSource.roleKeyPrefix + r.Id] = getUserNamesByRoleId(u2csByCommission, r.Id, users);
                });
                const childCommissions = commissionsWithParents.filter((c) => c.ParentId === commission.Id);
                const department = commission.DepartmentId ? departments[commission.DepartmentId] : null;
                const departmentLabel = (d: DepartmentEntity) => d && ((d.Name || '') + (d.Spartennummer ? (' (' + d.Spartennummer+')') : ''));
                return {
                    DepartmentId: commission.DepartmentId,
                    Department: department ? departmentLabel(department) : null,
                    ChildCommissionDepartments: childCommissions.map((c) => c.DepartmentId ? departmentLabel(departments[c.DepartmentId]) : null).filter(v => !!v).join(', ') || '',
                    isChildCommission: !!commission.ParentId,
                    isParentCommission: childCommissions.length > 0,
                    isParentCommissionTooltip: 'Diesem ' + singleCommissionLabel + ' sind andere zugeordnet: ' + childCommissions.map((c) => c.Auftragsnummer).join(', '),
                    CommissionTypeId: commission.CommissionTypeId,
                    CustomerId: commission.CustomerId,
                    freeTextField1: commission.FreeTextField1,
                    AssignUserIds: u2csByCommission.filter((u2c) => u2c.Assignee).map((u2c) => u2c.UserId),
                    UserId: commission.UserId,
                    PartnerOfficeId: commission.PartnerOfficeId,
                    PartnerOffice: commission.PartnerOfficeId ? partnerOffices[commission.PartnerOfficeId]?.Name : null,
                    StatusBackofficeId: commission.StatusBackofficeId,
                    id: commission.Id,
                    AuftragsNr: commission.Auftragsnummer || '-',
                    Auftrag: commission,
                    InterneNr: commission.InterneNummer || '-',
                    SchadenNr: commission.Schadennummer || '-',
                    VersicherungsNr: commission.Versicherungsnummer || '-',
                    StatusBackoffice: StatusBackoffice?.Name,
                    LastDeadline: commission.LastDeadline?.toLocaleDateString('de-DE'),
                    StartDate: commission.StartDate?.toLocaleDateString('de-DE') || commission.PlannedStartDate?.toLocaleDateString('de-DE'),
                    EndDate: commission.EndDate?.toLocaleDateString('de-DE') || commission.PlannedEndDate?.toLocaleDateString('de-DE'),
                    AbgabeterminAG: commission.AbgabeterminAG?.toLocaleDateString('de-DE'),
                    Auftraggeber: customer?.DisplayName || '-',
                    routerLink,
                    Address: getAddressString(commission),
                    clickable: true,
                    cssStyle: `--app-clickable-background-color: ${commission.GetBackgroundColor(StatusBackoffice?.IsCompleted, StatusBackoffice?.Color)};`,
                    cssClass: 'app-clickable-background',
                    Auftragstyp: commissonTypes[commission.CommissionTypeId]?.Name || '-',
                    Description: commission.Description,
                    Author: (commission.UserId && users[commission.UserId]?.DisplayName) || '',
                    CreatedAt: commission.CreatedAt.toLocaleDateString('de-DE'),
                    roleCols,
                    lastComment$: this.commentsAfterFetched$.pipe(
                        switchMap(() => this.store.select(getCommentByEntityTypeEntityId({ entityId: commission.Id, entityType: CommentEntityTypeEnum.Commission }))),
                        map((comments) => comments.sort((a, b) => b.CreatedAt.getTime() - a.CreatedAt.getTime())[0]?.htmlText),
                    ),
                    offerSum$: of(null).pipe(
                        tap(() => this.ledgerImportsFetch$.next(true)),
                        switchMap(() => this.ledgerImports$),
                        map((ledgerImports) => {
                            if (ledgerImports === null) {
                                return null;
                            }
                            const li = ledgerImports
                                .filter((li) => li.CommissionId === commission.Id && (li.DocumentType === LedgerImportDocumentTypes.OfferConfirmation || li.DocumentType === LedgerImportDocumentTypes.Offer))
                                .sort((a, b) => {
                                    if (a.DocumentType === b.DocumentType) {
                                        if ((a.Status === LedgerImportStatusEnum.AngebotAkzeptiert) === (b.Status === LedgerImportStatusEnum.AngebotAkzeptiert)) {
                                            return b.CreatedAt.getTime() - a.CreatedAt.getTime();
                                        }
                                        return a.Status === LedgerImportStatusEnum.AngebotAkzeptiert ? -1 : 1;
                                    }
                                    return a.DocumentType === LedgerImportDocumentTypes.OfferConfirmation ? -1 : 1;
                                })[0];
                            if (li) {
                                return formatCurrency((li.ConsolidatedNettoAmount || 0) / 100, this.local, getCurrencySymbol(li.ConsolidatedCurrencyCode, 'narrow'), li.ConsolidatedCurrencyCode);
                            }
                            return ' ';
                        }),
                    ),
                    colorBarValue: getCommissionColorValue(commission, commissonTypes[commission.CommissionTypeId]),
                    OfferSubmissionDate: commission.OfferSubmissionDate?.toLocaleString('de-DE', {
                        day: 'numeric',
                        month: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                    NegotiationAt: commission.NegotiationAt?.toLocaleString('de-DE', {
                        day: 'numeric',
                        month: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                    CostCenter: commission.CostCenter,
                    hasRelevantRoleOrIsAssigned: commission.ImAssigned || u2csByCommission.some(u2c => u2c.UserId === user.Id && (u2c.Assignee || (u2c.RoleId && !ignoredRoles.includes(u2c.RoleId)))),
                    CompleteBusinessVolume: commission?.CompleteBusinessVolume ? formatCurrency((commission.CompleteBusinessVolume || 0) / 100, this.local, getCurrencySymbol("EUR", 'narrow'), "EUR") : null,
                    RelevantUserIds: u2csByCommission
                        .filter((u2c) =>
                            u2c.Assignee ||
                            (u2c.RoleId && !ignoredRoles.includes(u2c.RoleId))
                        )
                        .map((u2c) => u2c.UserId),
                };
            });
        }),
    );
    private ledgerImportsFetch$ = new BehaviorSubject(false);
    private ledgerImports$ = new BehaviorSubject<LedgerImportEntity[]|null>([]);
    private dataSubscription: Subscription;
    public length$ = new BehaviorSubject<number>(0);
    constructor(
        private store: Store<State>,
        private cls: CustomLabelService,
        protected customerDataService: CustomerDataService,
        private local: string,

        private commentResolver: CommentResolver,
        public FilterValues$: BehaviorSubject<{
            [FilterTypes.Customers]: number|null;
            [FilterTypes.Editor]: IFilterTypeSearchMultiSelectValue[];
            [FilterTypes.PartnerOfficeId]: IFilterTypeSearchMultiSelectValue[];
            [FilterTypes.Status]: IFilterTypeSearchMultiSelectValue[];
            [FilterTypes.CommissionType]: IFilterTypeSearchMultiSelectValue[];
            [FilterTypes.Departments]: IFilterTypeSearchMultiSelectValue<number>[];
            [FilterTypes.HideSubCommissions]: boolean;
            [FilterTypes.HasRelevantRoleOrIsAssigned]: boolean;
        }>,
        protected lederImportDataService: LedgerImportDataService
    ) {
        super();
    }

    connect(): BehaviorSubject<CommissionListItem[]> {
        this.dataSubscription = this.data$
            .pipe(
                switchMap((data) =>
                    combineLatest([
                        this.FilterValues$.pipe(
                            tap(val => console.log('FILTERs', val)),
                            map((filter) => ({
                                [FilterTypes.Customers]: filter[FilterTypes.Customers],
                                [FilterTypes.Editor]: filter[FilterTypes.Editor] && filter[FilterTypes.Editor].length ? filter[FilterTypes.Editor] : null,
                                [FilterTypes.PartnerOfficeId]: filter[FilterTypes.PartnerOfficeId] && filter[FilterTypes.PartnerOfficeId].length ? filter[FilterTypes.PartnerOfficeId] : null,
                                [FilterTypes.Status]: filter[FilterTypes.Status] && filter[FilterTypes.Status].length ? filter[FilterTypes.Status] : null,
                                [FilterTypes.CommissionType]: filter[FilterTypes.CommissionType] && filter[FilterTypes.CommissionType].length ? filter[FilterTypes.CommissionType] : null,
                                [FilterTypes.Departments]: filter[FilterTypes.Departments] && filter[FilterTypes.Departments].length ? filter[FilterTypes.Departments] : null,
                                [FilterTypes.HideSubCommissions]: isNotNullOrUndefined(filter[FilterTypes.HideSubCommissions]) ? filter[FilterTypes.HideSubCommissions] : null,
                                [FilterTypes.HasRelevantRoleOrIsAssigned]: isNotNullOrUndefined(filter[FilterTypes.HasRelevantRoleOrIsAssigned]) ? filter[FilterTypes.HasRelevantRoleOrIsAssigned] : null,
                            })),
                            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                        ),
                        this.ledgerImportsFetch$.pipe(distinctUntilChanged()),
                        this.matPaginator.page.pipe(startWith(null)),
                        this.marSort.sortChange.pipe(startWith(null)),
                        this.searchQuery$.pipe(
                            debounceTime(SearchQueriesDebounceTime),
                            startWith(''),
                            map(() => (this.searchQuery$.value || '').trim().toLocaleLowerCase()),
                            distinctUntilChanged(),
                        ),
                    ]).pipe(
                        map(([filter, loadLedgerImports, _1, _2, searchQuery]) => {
                            let filteredData = this.getSearchedData(this.getFilteredData([...data], filter), searchQuery);
                            this.length$.next(filteredData.length);
                            let out =  this.getPagedData(this.getSortedData(filteredData), this.matPaginator.pageSize, this.matPaginator.pageIndex);
                            if (loadLedgerImports) {
                                this.ledgerImports$.next(null);
                                this.lederImportDataService.getLedgerImportsFromCommissions$(out.map(o => +o.id)).then(ledgerImports => {
                                    this.ledgerImports$.next(ledgerImports);
                                }).catch(err => console.error(err));
                            }
                            return out
                        })
                    ),
                ),
            )
            .subscribe((data) => {
                this.data = data;
            });
        return super.connect();
    }

    disconnect(): void {
        this.dataSubscription?.unsubscribe();
        super.disconnect();
    }

    getSortedData(data: CommissionListItem[]): CommissionListItem[]  {
        if (!this.marSort || !this.marSort.active || this.marSort.direction === '') {
            return data;
        }
        const isAsc = this.marSort.direction === 'asc';
        return data.sort((a, b) => {
            return compare(this.sortingDataAccessor(a, this.marSort.active), this.sortingDataAccessor(b, this.marSort.active), isAsc);
        });
    }
    sortingDataAccessor = (object: CommissionListItem, key: string) => {
        switch (key as keyof CommissionListItem) {
            case 'AuftragsNr':
            case 'InterneNr':
            case 'SchadenNr':
            case 'VersicherungsNr':
            case 'Description':
            case 'Author':
                return `${object[key]}`.trim().toLowerCase();
            case 'StartDate':
                return object.Auftrag.StartDate?.getTime() || object.Auftrag.PlannedStartDate?.getTime();
            case 'EndDate':
                return object.Auftrag.EndDate?.getTime() || object.Auftrag.PlannedEndDate?.getTime();
            case 'CreatedAt':
                return object.Auftrag.CreatedAt?.getTime();
            case 'AbgabeterminAG':
                return object.Auftrag.AbgabeterminAG?.getTime();
            case 'LastDeadline':
                return object.Auftrag.LastDeadline?.getTime();
            case 'CompleteBusinessVolume':
                return object.Auftrag.CompleteBusinessVolume ?? 0;
            case 'Auftrag':
                return '';
            default:
                return object[key] ? object[key].toString() : '';
        }
    };
    filterPredicate = (data: CommissionListItem, searchTerm: string) => {
        console.log('filterPredicate', data, searchTerm);
        return [data.Auftrag.CustomerName, data.AuftragsNr, data.InterneNr, data.VersicherungsNr, data.Address, data.Auftrag.Description].some((value) => stringSearch(value || '', searchTerm));
    };

    private getFilteredData(data: CommissionListItem[], filter: typeof this.FilterValues$.value): CommissionListItem[] {
        if (filter[FilterTypes.HasRelevantRoleOrIsAssigned]) {
            data = data.filter((c) => c.hasRelevantRoleOrIsAssigned);
        }
        if (filter[FilterTypes.HideSubCommissions]) {
            data = data.filter((c) => !c.isChildCommission);
        }
        if (filter.Customers != null) {
            data = data.filter((c) => c.CustomerId == filter[FilterTypes.Customers]);
        }
        if (filter.commissionType?.length && isArray(filter.commissionType)) {
            data = data.filter((c) => filter.commissionType.some((ct) => ct.id === c.CommissionTypeId));
        }
        if (filter.editor?.length && isArray(filter.editor)) {
            data = data.filter((c) => (c.RelevantUserIds.length ? filter.editor.some((e) => c.RelevantUserIds.includes(Number(e.id))) : filter.editor.some((e) => Number(e.id) === c.UserId)));
        }
        if (filter.partnerOfficeId?.length && isArray(filter.partnerOfficeId)) {
            data = data.filter((c) => filter.partnerOfficeId.some((po) => Number(po.id) === c.PartnerOfficeId));
        }
        if (filter[FilterTypes.Departments]?.length && isArray(filter[FilterTypes.Departments])) {
            data = data.filter((c) => filter[FilterTypes.Departments].some((dp) => dp.id === c.DepartmentId));
        }
        if (filter.status?.length && isArray(filter.status)) {
            data = data.filter((c) =>
                filter.status.some((s) => {
                   // fixes old number values from localstorage
                    return (s.id+'').split('|').includes(c.StatusBackofficeId+'');
                })
            );
        }
        return data;
    }

    protected getPagedData(data: CommissionListItem[], pageSize: number, page: number): CommissionListItem[] {
        return data.slice(page * pageSize, (page + 1) * pageSize);
    }
    protected getSearchedData(data: CommissionListItem[], searchQuery: string): CommissionListItem[] {
        if (!searchQuery) {
            return data;
        }
        return data.filter((c) => this.filterPredicate(c, searchQuery));
    }
}
function compare(a: string | number, b: string | number, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
